import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
} from '@mui/material';
import {
  Add as AddIcon,
  AttachMoney as MoneyIcon,
  AccessTime as TimeIcon,
  CardMembership as FitnessIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import JuntoApiService, { Membership, ClassCategory } from '../services/api';
import EntityActionButtons from '../components/EntityActionButtons/EntityActionButtons';

const MembershipsPage: React.FC = () => {
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [classCategories, setClassCategories] = useState<ClassCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [includeInactive, setIncludeInactive] = useState(false);
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMembership, setEditingMembership] = useState<Membership | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    cost: '',
    period_type: 'monthly',
    category_usage_limits: {} as Record<string, number | string>,
    tags: [] as string[],
  });
  
  // Validation state for category limits
  const [categoryLimitErrors, setCategoryLimitErrors] = useState<Record<string, string>>({});
  const [categoryLimitRawValues, setCategoryLimitRawValues] = useState<Record<string, string>>({});

  // Validation helper for category limits
  const validateCategoryLimit = (value: string): { isValid: boolean; sanitizedValue: number | string | null; error?: string } => {
    const trimmedValue = value.trim();
    
    // Empty value is valid (removes the limit)
    if (trimmedValue === '' || trimmedValue === '0') {
      return { isValid: true, sanitizedValue: null };
    }
    
    // Check for "unlimited" (case insensitive)
    if (trimmedValue.toLowerCase() === 'unlimited') {
      return { isValid: true, sanitizedValue: 'unlimited' };
    }
    
    // Check for positive integer
    const numValue = parseInt(trimmedValue);
    if (!isNaN(numValue) && numValue > 0 && numValue.toString() === trimmedValue) {
      return { isValid: true, sanitizedValue: numValue };
    }
    
    // Invalid input
    return { 
      isValid: false, 
      sanitizedValue: null, 
      error: 'Enter "unlimited" or a positive number' 
    };
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [membershipsData, categoriesData] = await Promise.all([
        JuntoApiService.getMemberships(includeInactive),
        JuntoApiService.getClassCategories(includeInactive),
      ]);
      setMemberships(membershipsData);
      setClassCategories(categoriesData);
      setError(null);
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Failed to load memberships');
    } finally {
      setLoading(false);
    }
  }, [includeInactive]);

  useEffect(() => {
    loadData();
  }, [loadData]);


  const getCategoryName = (categoryId: string): string => {
    const category = classCategories.find(cat => cat.id === categoryId);
    return category ? category.name : categoryId;
  };

  const openCreateDialog = () => {
    setEditingMembership(null);
    setFormData({
      name: '',
      description: '',
      cost: '',
      period_type: 'monthly',
      category_usage_limits: {},
      tags: [],
    });
    setCategoryLimitErrors({});
    setCategoryLimitRawValues({});
    setDialogError(null);
    setDialogOpen(true);
  };

  const openEditDialog = (membership: Membership) => {
    setEditingMembership(membership);
    setFormData({
      name: membership.name,
      description: membership.description || '',
      cost: membership.cost.toString(),
      period_type: membership.period_type,
      category_usage_limits: membership.category_usage_limits,
      tags: membership.tags || [],
    });
    
    // Initialize raw values for category limits from existing data
    const rawValues: Record<string, string> = {};
    Object.entries(membership.category_usage_limits).forEach(([categoryId, value]) => {
      rawValues[categoryId] = value.toString();
    });
    setCategoryLimitRawValues(rawValues);
    setCategoryLimitErrors({});
    setDialogError(null);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingMembership(null);
    setCategoryLimitErrors({});
    setCategoryLimitRawValues({});
    setDialogError(null);
  };

  const handleSave = async () => {
    try {
      setDialogError(null);
      
      // Validate all category limits before saving
      const validationErrors: Record<string, string> = {};
      const finalCategoryLimits: Record<string, number | string> = {};
      
      // Validate current raw values
      Object.entries(categoryLimitRawValues).forEach(([categoryId, rawValue]) => {
        const validation = validateCategoryLimit(rawValue);
        if (!validation.isValid) {
          validationErrors[categoryId] = validation.error || 'Invalid input';
        } else if (validation.sanitizedValue !== null) {
          finalCategoryLimits[categoryId] = validation.sanitizedValue;
        }
      });
      
      // Also include any existing valid limits that weren't changed
      Object.entries(formData.category_usage_limits).forEach(([categoryId, value]) => {
        if (!(categoryId in categoryLimitRawValues)) {
          finalCategoryLimits[categoryId] = value;
        }
      });
      
      // If there are validation errors, show them and stop
      if (Object.keys(validationErrors).length > 0) {
        setCategoryLimitErrors(validationErrors);
        setDialogError('Please fix the validation errors in category limits before saving.');
        return;
      }
      
      const membershipData = {
        ...formData,
        id: editingMembership?.id || `membership-${Date.now()}`,
        cost: parseFloat(formData.cost),
        category_usage_limits: finalCategoryLimits,
        valid_location_ids: [], // For now, empty array
        created_at: editingMembership?.created_at || new Date().toISOString(),
      };

      if (editingMembership) {
        // Update existing membership
        await JuntoApiService.updateMembership(membershipData as Membership);
      } else {
        // Create new membership
        await JuntoApiService.createMembership(membershipData);
      }
      
      await loadData();
      closeDialog();
    } catch (err) {
      console.error('Failed to save membership:', err);
      setDialogError('Failed to save membership. Please check your input.');
    }
  };


  const handleCategoryLimitChange = (categoryId: string, value: string) => {
    // Store the raw input value to allow free typing
    setCategoryLimitRawValues({
      ...categoryLimitRawValues,
      [categoryId]: value
    });
    
    // Clear any existing error for this field while typing
    if (categoryLimitErrors[categoryId]) {
      const newErrors = { ...categoryLimitErrors };
      delete newErrors[categoryId];
      setCategoryLimitErrors(newErrors);
    }
  };

  const handleCategoryLimitBlur = (categoryId: string) => {
    const rawValue = categoryLimitRawValues[categoryId] || '';
    const validation = validateCategoryLimit(rawValue);
    
    if (validation.isValid) {
      // Update the form data with the sanitized value
      const newLimits = { ...formData.category_usage_limits };
      if (validation.sanitizedValue === null) {
        delete newLimits[categoryId];
      } else {
        newLimits[categoryId] = validation.sanitizedValue;
      }
      setFormData({ ...formData, category_usage_limits: newLimits });
      
      // Clear any error
      if (categoryLimitErrors[categoryId]) {
        const newErrors = { ...categoryLimitErrors };
        delete newErrors[categoryId];
        setCategoryLimitErrors(newErrors);
      }
    } else {
      // Set validation error
      setCategoryLimitErrors({
        ...categoryLimitErrors,
        [categoryId]: validation.error || 'Invalid input'
      });
    }
  };

  const formatPeriodType = (periodType: string) => {
    return periodType.charAt(0).toUpperCase() + periodType.slice(1);
  };


  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading memberships...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Memberships</Typography>
        <Box display="flex" alignItems="center" gap={2}>
          <FormControlLabel
            control={
              <Switch
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
                icon={<VisibilityOffIcon />}
                checkedIcon={<VisibilityIcon />}
              />
            }
            label="Show Inactive"
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreateDialog}
          >
            Create Membership
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
          <Button size="small" onClick={loadData} sx={{ ml: 2 }}>
            Retry
          </Button>
        </Alert>
      )}

      {/* Quick Stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <FitnessIcon color="primary" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">{memberships.length}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Plans
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <MoneyIcon color="success" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">
                    ${Math.round(memberships.reduce((sum, m) => sum + m.cost, 0) / memberships.length || 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Avg Price
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <TimeIcon color="warning" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">
                    {memberships.filter(m => m.period_type === 'monthly').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Monthly Plans
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <FitnessIcon color="info" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">
                    {memberships.filter(m => m.is_active).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active Plans
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Cost</TableCell>
                <TableCell>Period</TableCell>
                <TableCell>Usage Limits</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {memberships.map((membership) => (
                <TableRow 
                  key={membership.id} 
                  hover 
                  sx={{ 
                    opacity: membership.is_active ? 1 : 0.6,
                    backgroundColor: membership.is_active ? 'inherit' : 'action.hover'
                  }}
                >
                  <TableCell>
                    <Box>
                      <Typography variant="body1" fontWeight="medium">
                        {membership.name}
                      </Typography>
                      {membership.description && (
                        <Typography variant="body2" color="text.secondary">
                          {membership.description}
                        </Typography>
                      )}
                      {membership.tags && membership.tags.length > 0 && (
                        <Box mt={1}>
                          {membership.tags.map((tag, index) => (
                            <Chip
                              key={index}
                              label={tag}
                              size="small"
                              variant="outlined"
                              color="secondary"
                              sx={{ mr: 0.5 }}
                            />
                          ))}
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <MoneyIcon color="success" sx={{ mr: 1 }} />
                      <Typography variant="body1" fontWeight="medium">
                        ${membership.cost}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={formatPeriodType(membership.period_type)}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {Object.keys(membership.category_usage_limits).length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          No limits set
                        </Typography>
                      ) : (
                        Object.entries(membership.category_usage_limits).map(([categoryId, limit]) => {
                          const categoryName = getCategoryName(categoryId);
                          const limitText = limit === 'unlimited' ? 'Unlimited' : `${limit}`;
                          return (
                            <Chip
                              key={categoryId}
                              label={`${categoryName}: ${limitText}`}
                              size="small"
                              variant="outlined"
                              color="primary"
                              sx={{ mr: 0.5, mb: 0.5 }}
                            />
                          );
                        })
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={membership.is_active ? 'Active' : 'Inactive'}
                      color={membership.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <EntityActionButtons
                      entity={membership}
                      entityType="memberships"
                      entityDisplayName="Membership"
                      onEdit={openEditDialog}
                      onRefresh={loadData}
                      renderEntityInHistory={(entity) => (
                        <Box>
                          <Typography variant="body1">
                            <strong>Name:</strong> {entity.name}
                          </Typography>
                          <Typography variant="body1">
                            <strong>Cost:</strong> ${entity.cost}
                          </Typography>
                          <Typography variant="body1">
                            <strong>Period:</strong> {formatPeriodType(entity.period_type)}
                          </Typography>
                          <Typography variant="body1">
                            <strong>Category Limits:</strong> {Object.keys(entity.category_usage_limits).length || 'None'}
                          </Typography>
                        </Box>
                      )}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingMembership ? 'Edit Membership' : 'Create New Membership'}
        </DialogTitle>
        <DialogContent>
          {dialogError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {dialogError}
            </Alert>
          )}
          
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Cost"
                type="number"
                value={formData.cost}
                onChange={(e) => setFormData({ ...formData, cost: e.target.value })}
                required
                inputProps={{ min: 0, step: 0.01 }}
                InputProps={{
                  startAdornment: <Typography>$</Typography>,
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Tags (comma-separated)"
                defaultValue={formData.tags.join(', ')}
                onBlur={(e) => {
                  const tags = e.target.value
                    .split(',')
                    .map(tag => tag.trim())
                    .filter(tag => tag.length > 0);
                  setFormData({ ...formData, tags });
                }}
                placeholder="e.g. unlimited, premium, popular"
                helperText="Enter tags separated by commas for categorization and filtering"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Period Type</InputLabel>
                <Select
                  value={formData.period_type}
                  onChange={(e) => setFormData({ ...formData, period_type: e.target.value })}
                >
                  <MenuItem value="weekly">Weekly</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                  <MenuItem value="quarterly">Quarterly</MenuItem>
                  <MenuItem value="annually">Annually</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Class Category Usage Limits
              </Typography>
              <Grid container spacing={2}>
                {classCategories.map((category) => (
                  <Grid item xs={12} md={6} key={category.id}>
                    <TextField
                      fullWidth
                      label={`${category.name} Limit`}
                      value={categoryLimitRawValues[category.id] || formData.category_usage_limits[category.id]?.toString() || ''}
                      onChange={(e) => handleCategoryLimitChange(category.id, e.target.value)}
                      onBlur={() => handleCategoryLimitBlur(category.id)}
                      placeholder="unlimited or number"
                      helperText={categoryLimitErrors[category.id] || "Enter 'unlimited' or a number"}
                      error={!!categoryLimitErrors[category.id]}
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {editingMembership ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MembershipsPage;